import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ApiInvite } from 'api/invite/types/ApiInvite';
import { Box } from 'components/Box/Box';
import { CenteredContent } from 'components/CenteredContent/CenteredContent';
import { IdealistLogoOnDemand } from 'components/Logo/IdealistLogoOnDemand';
import { Text } from 'components/Text/Text';
import { SignupFormContainer } from 'containers/SignupFormContainer';
import { allowRedirect } from 'utils/allowRedirect';
import { trackPageView } from 'utils/analytics/track/trackPageView';
import { isPathOnlyUrl } from 'utils/isPathOnlyUrl';
import { setDocumentTitle } from 'utils/meta/setDocumentTitle';

type Props = {
  invite: ApiInvite | null;
};

export function SignupPage({ invite }: Props) {
  const navigate = useNavigate();
  const location = useLocation<{
    redirectTo?: string | null;
    email?: string;
    savedListing: { id: string; type: string; name: string };
    search: boolean;
  }>();

  const [searchParams] = useSearchParams();
  const savedListing = location.state?.savedListing;
  const email = location.state?.email;
  const redirectTo = searchParams.get('to') || location.state?.redirectTo;

  useEffect(() => {
    setDocumentTitle(getText('Sign Up'));
    trackPageView('Sign Up', {
      page_type: 'Sign Up',
    });
  }, []);

  const redirect = () => {
    let redirectToValue = redirectTo;

    if (!redirectToValue || !allowRedirect(redirectToValue)) {
      redirectToValue = `/${CURRENT_LOCALE}/`;
    }

    if (!isPathOnlyUrl(redirectToValue)) {
      window.location.assign(redirectToValue);
      return;
    }

    navigate(redirectToValue);
  };

  return (
    <CenteredContent>
      <Box textAlign="center">
        <IdealistLogoOnDemand height={82} />
      </Box>

      <Text
        fontSize="24px"
        fontWeight="semiBold"
        color="mediumContentGrey"
        textAlign="center"
        mb="8px"
      >
        {
          /* TRANSLATORS: signup page title */
          getText('Sign Up')
        }
      </Text>

      <SignupFormContainer
        analyticsTitle="[main] Signup page"
        usageContext="page"
        redirectTo={redirectTo}
        email={email}
        invite={invite}
        savedListing={savedListing}
        onSignup={redirect}
        onLogin={undefined}
        subscribedSubsite={undefined}
      />
    </CenteredContent>
  );
}
